// Passwordless sign-in: email → 6-digit code (or the one-tap link in the email,
// which lands here with ?email=&token= and signs in automatically).
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { errorMessage } from "../lib/api";
import { Pashmak } from "../components/Pashmak";
import { Fa } from "../components/Phrase";
import { Spinner } from "../components/ui";
import { IMail, IArrowLeft, IPhone, IChat, IHeadphones, IPath } from "../components/icons";

const EMAIL_KEY = "pashmak.lastEmail";

export function Login() {
  const { startSignIn, verify } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = params.get("next") || "/";
  const [step, setStep] = useState<"email" | "code" | "link">(params.get("token") ? "link" : "email");
  const [email, setEmail] = useState(() => params.get("email") || safeGet(EMAIL_KEY) || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resent, setResent] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);
  const linkTried = useRef(false); // links are single-use: never verify twice (StrictMode runs effects twice)

  // One-tap link from the email.
  useEffect(() => {
    const token = params.get("token");
    const em = params.get("email");
    if (!token || !em || linkTried.current) return;
    linkTried.current = true;
    verify({ email: em, token }).then(
      () => nav(next, { replace: true }),
      (e) => {
        setStep("email");
        setError(e?.status === 401 ? "That sign-in link has expired or was already used. Send a new one below." : errorMessage(e));
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      await startSignIn(email);
      safeSet(EMAIL_KEY, email.trim());
      setStep("code");
      setCode("");
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(c = code) {
    if (c.length !== 6) return;
    setError("");
    setBusy(true);
    try {
      await verify({ email, code: c });
      nav(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setCode("");
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <style>{css}</style>
      <section className="login-pitch" aria-hidden="true">
        <div className="pitch-inner">
          <Pashmak size={180} mood="happy" />
          <h1>
            Learn Persian by <em>talking</em>.
          </h1>
          <Fa className="pitch-fa">فارسی رو با حرف زدن یاد بگیر</Fa>
          <ul>
            <li><IPhone /> Live voice calls and role-plays: a Tehran taxi, the bazaar, meeting the in-laws</li>
            <li><IChat /> Text with Pashmak and get gentle corrections on every message</li>
            <li><IHeadphones /> Short stories at your level, voiced by a whole cast</li>
            <li><IPath /> Bite-size lessons and the alphabet, with real recorded audio</li>
          </ul>
        </div>
      </section>

      <main className="login-form">
        <div className="form-inner">
          <div className="brandline">
            <Pashmak size={44} plain />
            <strong>Pashmak</strong> <Fa>پشمک</Fa>
          </div>

          {step === "link" && (
            <div className="stack" style={{ alignItems: "center", textAlign: "center" }}>
              <Spinner />
              <p className="sub">Signing you in…</p>
            </div>
          )}

          {step === "email" && (
            <form onSubmit={send} className="stack loose">
              <div className="stack tight">
                <h1>Sign in or create your account</h1>
                <p className="sub">We'll email you a code. No password needed. Your progress syncs everywhere you learn.</p>
              </div>
              <label className="field">
                Email
                <input
                  className="input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-invalid={!!error}
                  aria-describedby={error ? "login-error" : undefined}
                />
              </label>
              {error && <p id="login-error" role="alert" className="error">{error}</p>}
              <button className="btn lg block" disabled={busy || !email.includes("@")}>
                {busy ? <Spinner /> : <IMail />} Email me a sign-in code
              </button>
              <p className="tiny muted">
                By continuing you agree to the <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>. Free to start: lessons, the alphabet and
                daily practice are free; Pro unlocks unlimited practice.
              </p>
            </form>
          )}

          {step === "code" && (
            <form
              className="stack loose"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCode();
              }}
            >
              <button type="button" className="btn ghost sm" style={{ alignSelf: "flex-start" }} onClick={() => setStep("email")}>
                <IArrowLeft width={16} height={16} /> Change email
              </button>
              <div className="stack tight">
                <h1>Check your email</h1>
                <p className="sub">
                  We sent a 6-digit code to <strong>{email}</strong>. Enter it below, or tap the button in the email.
                </p>
              </div>
              <label className="field">
                Sign-in code
                <input
                  ref={codeRef}
                  className="input code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  aria-invalid={!!error}
                  aria-describedby={error ? "login-error" : undefined}
                  onChange={(e) => {
                    const c = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setCode(c);
                    if (c.length === 6) void submitCode(c);
                  }}
                  placeholder="••••••"
                />
              </label>
              {error && <p id="login-error" role="alert" className="error">{error}</p>}
              <button className="btn lg block" disabled={busy || code.length !== 6}>
                {busy && <Spinner />} Sign in
              </button>
              <p className="small sub">
                Didn't get it? Check spam, or{" "}
                <button
                  type="button"
                  className="linkbtn"
                  disabled={busy || resent >= 3}
                  onClick={() => {
                    setResent((n) => n + 1);
                    void send();
                  }}
                >
                  send a new code
                </button>
                .
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function safeGet(k: string) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}

const css = `
.login{min-height:100dvh;display:grid;grid-template-columns:1.1fr 1fr}
.login-pitch{background:radial-gradient(120% 80% at 20% 10%,var(--firuze-soft),transparent 60%),radial-gradient(90% 70% at 90% 90%,var(--gold-soft),transparent 60%),var(--bg-2);display:grid;place-items:center;padding:48px}
.pitch-inner{max-width:520px;display:flex;flex-direction:column;gap:16px}
.pitch-inner h1{font-size:clamp(34px,4vw,48px)}
.pitch-inner h1 em{font-style:normal;color:var(--firuze)}
.pitch-fa{font-size:24px;color:var(--gold)}
.pitch-inner ul{list-style:none;padding:0;margin:8px 0 0;display:flex;flex-direction:column;gap:12px;color:var(--sub);font-weight:600}
.pitch-inner li{display:flex;gap:12px;align-items:flex-start}
.pitch-inner li svg{width:22px;height:22px;color:var(--firuze);flex:none;margin-top:1px}
.login-form{display:grid;place-items:center;padding:32px 20px}
.form-inner{width:100%;max-width:400px}
.brandline{display:flex;align-items:center;gap:10px;font-size:20px;margin-bottom:28px}
.brandline .fa{color:var(--gold);line-height:1}
.input.code{font:800 28px/1 ui-monospace,Menlo,monospace;letter-spacing:12px;text-align:center;min-height:62px}
.error{color:var(--rose);font-weight:700;font-size:14px}
.linkbtn{border:0;background:none;padding:0;color:var(--firuze);font-weight:700;cursor:pointer;text-decoration:underline}
.linkbtn:disabled{opacity:.5;cursor:default}
@media (max-width:860px){.login{grid-template-columns:1fr}.login-pitch{display:none}}
`;
